import { Icon, Image, InlineStack, PlainButton, Stack, Strong, Text } from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { toWhatsappPreviewComponents, type WhatsappPreviewComponent } from '../utils/whatsapp-template';
import * as styles from './delivery-message-preview.css';

type WhatsappMessagePreviewProps = { body: string };

/** WhatsApp template preview — the message bubble with header/body/footer text and buttons. */
export function WhatsappMessagePreview({ body }: WhatsappMessagePreviewProps) {
  const components = toWhatsappPreviewComponents(body);
  const content = components.filter((c) => c.type !== 'button');
  const buttons = components.filter((c) => c.type === 'button');

  return (
    <InlineStack alignInline="center">
      <Stack className={`${styles.whatsappBubble} fs-mask`} pt="100" data-test-delivery-preview-whatsapp>
        {content.map((component, index) => (
          <WhatsappContent key={index} component={component} />
        ))}
        {buttons.map((button, index) => (
          <PlainButton key={index} className={styles.whatsappButton}>
            <InlineStack alignInline="center" gap="100">
              <Icon name="link" />
              {button.text}
            </InlineStack>
          </PlainButton>
        ))}
      </Stack>
    </InlineStack>
  );
}

type WhatsappContentProps = { component: WhatsappPreviewComponent };

function WhatsappContent({ component }: WhatsappContentProps) {
  if (component.type === 'header') {
    return <WhatsappHeader component={component} />;
  }
  if (component.type === 'body') {
    return <Text className={styles.whatsappText}>{component.text}</Text>;
  }
  if (component.type === 'footer') {
    return (
      <Text className={styles.whatsappText} color="subtle">
        {component.text}
      </Text>
    );
  }
  return null;
}

function WhatsappHeader({ component }: WhatsappContentProps) {
  const { text, url, previewType } = component;

  if (!isEmptyString(text)) {
    return (
      <Strong className={styles.whatsappText} size="md">
        {text}
      </Strong>
    );
  }

  if (isEmptyString(url)) {
    return null;
  }
  const mediaUrl = url ?? '';

  if (previewType === 'image') {
    return <Image alt="WhatsApp header preview" src={mediaUrl} />;
  }
  if (previewType === 'video') {
    return (
      <InlineStack backgroundColor="subtle" alignBlock="center" alignInline="center" style={{ height: '150px' }}>
        <Icon name="play" size="xl" />
      </InlineStack>
    );
  }
  if (previewType === 'document') {
    return (
      <InlineStack backgroundColor="subtle" alignBlock="flex-end" p="100" style={{ height: '150px' }}>
        <InlineStack alignBlock="center">
          <Icon name="file-pdf" size="xl" />
          <Text>
            Document Title
            <br />
            123 KB - .file
          </Text>
        </InlineStack>
      </InlineStack>
    );
  }
  return <Text>Preview unavailable.</Text>;
}
